import PropTypes from 'prop-types'

/**
 * Transforms a selected tree level based on the given path and root object.
 * @param {string} path - The path to the selected tree level.
 * @param {object} root - The root object representing the tree structure.
 * @returns {Array} - An array of child elements of the selected tree level.
 *
 * @example
 * If path: ingredientsTree/animal/children/meat
 * Then result: [{name: 'meat', //...otherProps...//}, {name: 'fish', //...otherProps...//}, {name: 'milk', //...otherProps...//}]
 * If path: ingredientsTree/animal/children/meat/children
 * Then result: [{name: 'beef', //...otherProps...//}, {name: 'pork', //...otherProps...//}, {name: 'chicken', //...otherProps...//}]
 */
const transformSelectedTreeLevel = (root, path) => {
  if (!path || !root) return []

  // Split the path into an array of individual levels
  const paths = path.split('/').slice(1)

  const res = paths.reduce(
    ({ acc, parent }, path) => {
      // If the path is 'children', return the children array of the parent
      if (path === 'children')
        return { acc: parent?.[path] ?? [], parent: acc[path] }
      // Otherwise, return the child element at the specified path
      else return { acc, parent: acc[path] }
    },
    { acc: root, parent: root }
  )

  // Return an array of child elements of the selected tree level
  const nodes = Object.values(res.acc)
  // Sort by explicit order if present; otherwise keep original (creation/insertion) order
  const withOrder = nodes.filter((n) => typeof n?.order === 'number')
  const withoutOrder = nodes.filter((n) => typeof n?.order !== 'number')

  withOrder.sort((a, b) => a.order - b.order)
  // Fallback: sort by createdAt ascending when order is missing
  withoutOrder.sort((a, b) => {
    const aTime = a?._createdAt ? Date.parse(a._createdAt) : 0
    const bTime = b?._createdAt ? Date.parse(b._createdAt) : 0
    if (aTime !== bTime) return aTime - bTime
    // deterministic tie-breaker
    return String(a?._id || '').localeCompare(String(b?._id || ''))
  })

  return [...withOrder, ...withoutOrder]
}

transformSelectedTreeLevel.propTypes = {
  path: PropTypes.string.isRequired,
  root: PropTypes.object.isRequired
}

export default transformSelectedTreeLevel
